import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useApi } from './ApiContext';
import { useAuth } from './AuthContext';
import { useWithTokenRefresh } from '../utils/api';
import { QuotaTypes } from '@flashbacktech/flashbackclient/api';

interface QuotaUsage {
  storageUsed: number;
  storageLimit: number;
  storagePercentage: number;
  subscriptionName: string;
  subscriptionType: string;
  displayLimit: string;
}

interface QuotaData {
  quotaUsage: QuotaUsage | null;
  subscription: QuotaTypes.SubscriptionInfo | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
}

interface QuotaContextType {
  quotaData: QuotaData;
  refreshQuota: (forceRefresh?: boolean) => Promise<void>;
}

const QuotaContext = createContext<QuotaContextType | undefined>(undefined);

const CACHE_KEY = 'quota_context_cache';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

export const QuotaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { apiClient } = useApi();
  const { authState, globalData } = useAuth();
  const withTokenRefresh = useWithTokenRefresh();
  
  const [quotaData, setQuotaData] = useState<QuotaData>(() => {
    // Try to load from cache on mount
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const cacheData = JSON.parse(cached);
        const cacheAge = Date.now() - cacheData.timestamp;
        if (cacheAge < CACHE_DURATION) {
          return {
            quotaUsage: cacheData.quotaUsage,
            subscription: cacheData.subscription,
            isLoading: false,
            error: null,
            lastUpdated: cacheData.timestamp,
          };
        }
      }
    } catch (error) {
      console.error('[QuotaContext] Cache read error:', error);
    }
    
    return {
      quotaUsage: null,
      subscription: null,
      isLoading: false,
      error: null,
      lastUpdated: null,
    };
  });

  const refreshQuota = useCallback(async (forceRefresh = false) => {
    if (!authState?.token) {
      setQuotaData((prev) => ({ ...prev, isLoading: false, error: 'No authentication token' }));
      return;
    }

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const cacheData = JSON.parse(cached);
          const cacheAge = Date.now() - cacheData.timestamp;
          if (cacheAge < CACHE_DURATION) {
            setQuotaData({
              quotaUsage: cacheData.quotaUsage,
              subscription: cacheData.subscription,
              isLoading: false,
              error: null,
              lastUpdated: cacheData.timestamp,
            });
            // Dispatch event for backward compatibility
            try {
              localStorage.setItem('quotaData', JSON.stringify(cacheData.quotaUsage));
              window.dispatchEvent(new Event('quotaUpdated'));
            } catch (e) {
              // Ignore errors
            }
            return;
          }
        }
      } catch (error) {
        console.error('[QuotaContext] Cache read error:', error);
      }
    }

    setQuotaData((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await withTokenRefresh(() => apiClient.getUserQuota());

      if (response?.success && response?.quotaUsage && Array.isArray(response.quotaUsage)) {
        const trafficQuota = response.quotaUsage.find(
          (item: { capability?: { code?: string }; usage?: unknown }) => 
            item.capability && item.capability.code === 'traffic.max'
        );

        let quotaUsage: QuotaUsage | null = null;

        if (trafficQuota && trafficQuota.usage) {
          const current = trafficQuota.usage.current / 1024; // Convert MB to GB
          const max = trafficQuota.usage.max / 1024;

          let displayLimit: string;
          if (max >= 1024) {
            displayLimit = (max / 1024).toFixed(1) + ' TB';
          } else {
            displayLimit = max.toFixed(1) + ' GB';
          }

          const percentage = trafficQuota.usage.percentage || (max > 0 ? (current / max) * 100 : 0);

          let subscriptionType = '';
          if (response.subscription && response.subscription.name) {
            subscriptionType = response.subscription.name;
          }

          quotaUsage = {
            storageUsed: current,
            storageLimit: max,
            storagePercentage: percentage,
            subscriptionName: response.subscription?.name || '',
            subscriptionType: subscriptionType,
            displayLimit,
          };
        }

        const timestamp = Date.now();
        const newQuotaData: QuotaData = {
          quotaUsage,
          subscription: response.subscription,
          isLoading: false,
          error: null,
          lastUpdated: timestamp,
        };

        setQuotaData(newQuotaData);

        // Save to cache
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            quotaUsage,
            subscription: response.subscription,
            timestamp,
          }));
          // Backward compatibility
          localStorage.setItem('quotaData', JSON.stringify(quotaUsage));
          window.dispatchEvent(new Event('quotaUpdated'));
        } catch (error) {
          console.error('[QuotaContext] Cache write error:', error);
        }
      } else {
        setQuotaData((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Invalid quota response',
        }));
      }
    } catch (error) {
      console.error('[QuotaContext] Error loading quota:', error);
      setQuotaData((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load quota',
      }));
    }
  }, [authState?.token, apiClient, withTokenRefresh]);

  // Load quota when authenticated
  useEffect(() => {
    if (authState?.token && globalData?.isReady) {
      refreshQuota();
    }
  }, [authState?.token, globalData?.isReady, refreshQuota]);

  // Listen for quota refresh requests from other components
  useEffect(() => {
    const handleRefreshRequest = () => {
      refreshQuota(true);
    };

    window.addEventListener('refreshQuota', handleRefreshRequest);
    return () => window.removeEventListener('refreshQuota', handleRefreshRequest);
  }, [refreshQuota]);

  const value: QuotaContextType = {
    quotaData,
    refreshQuota,
  };

  return <QuotaContext.Provider value={value}>{children}</QuotaContext.Provider>;
};

export const useQuota = () => {
  const context = useContext(QuotaContext);
  if (!context) {
    throw new Error('useQuota must be used within a QuotaProvider');
  }
  return context;
};

